/**
 * AndroidChargingNotificationService - Ongoing notification for charging sessions
 * 
 * This service provides an Android equivalent to iOS Live Activities,
 * showing a persistent notification during active charging sessions with
 * real-time updates for kWh, time elapsed, and estimated cost.
 * 
 * Features:
 * - Ongoing/sticky notification that stays in notification shade
 * - Real-time updates as charging progresses
 * - Tappable to return to ChargingScreen
 * - Automatically dismissed when charge ends
 */

import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';

// ============================================================================
// Types
// ============================================================================

export interface ChargingNotificationParams {
  /** Name of the charger */
  chargerName: string;
  /** Order/session ID */
  orderId: string;
}

export interface ChargingNotificationUpdateParams {
  /** Current kWh charged */
  kwhCharged: number;
  /** Time elapsed in seconds */
  timeElapsedSeconds: number;
  /** Estimated cost so far */
  estimatedCost: number;
  /** Whether actively charging */
  isCharging: boolean;
  /** Human-readable status string */
  chargingStatus: string;
  /** Current charging rate in kW (optional) */
  chargingRateKw?: number;
}

// ============================================================================
// Constants
// ============================================================================

// Unique identifier for the charging notification
const CHARGING_NOTIFICATION_ID = 'meander-charging-session';

// Android notification channel for charging
const CHARGING_CHANNEL_ID = 'charging-session';

// ============================================================================
// AndroidChargingNotificationService Class
// ============================================================================

class AndroidChargingNotificationService {
  private isActive: boolean = false;
  private currentOrderId: string | null = null;
  private chargerName: string = '';
  private channelCreated: boolean = false;

  // --------------------------------------------------------------------------
  // Channel Setup
  // --------------------------------------------------------------------------

  /**
   * Create the notification channel for charging notifications (Android 8+)
   */
  private async ensureChannelExists(): Promise<void> {
    if (Platform.OS !== 'android' || this.channelCreated) {
      return;
    }

    try {
      await Notifications.setNotificationChannelAsync(CHARGING_CHANNEL_ID, {
        name: 'Charging Session',
        description: 'Shows ongoing charging session status',
        importance: Notifications.AndroidImportance.HIGH,
        vibrationPattern: [0, 0, 0, 0], // No vibration for updates
        lightColor: '#00D4AA', // Meander teal
        lockscreenVisibility: Notifications.AndroidNotificationVisibility.PUBLIC,
        bypassDnd: false,
        enableLights: true,
        enableVibrate: false,
        showBadge: true,
      });
      this.channelCreated = true;
      console.log('[AndroidChargingNotification] Channel created');
    } catch (error) {
      console.error('[AndroidChargingNotification] Failed to create channel:', error);
    }
  }

  // --------------------------------------------------------------------------
  // Notification Lifecycle
  // --------------------------------------------------------------------------

  /**
   * Start showing the charging notification
   */
  async startNotification(params: ChargingNotificationParams): Promise<boolean> {
    if (Platform.OS !== 'android') {
      return false;
    }

    console.log('[AndroidChargingNotification] Starting notification for order:', params.orderId);

    // Ensure channel exists
    await this.ensureChannelExists();

    // Dismiss any existing notification first
    await this.dismissNotification();

    this.chargerName = params.chargerName;
    this.currentOrderId = params.orderId;

    try {
      await Notifications.scheduleNotificationAsync({
        identifier: CHARGING_NOTIFICATION_ID,
        content: {
          title: '⚡ Charging Session Active',
          body: `${params.chargerName}\nConnecting...`,
          data: {
            type: 'charging_session',
            orderId: params.orderId,
            screen: 'ChargingScreen',
          },
          categoryIdentifier: 'charging',
          // Android-specific options
          sticky: true, // Makes it ongoing/non-dismissable
          autoDismiss: false,
          priority: Notifications.AndroidNotificationPriority.HIGH,
        },
        trigger: null, // Show immediately
      });

      this.isActive = true;
      console.log('[AndroidChargingNotification] Notification started');
      return true;
    } catch (error) {
      console.error('[AndroidChargingNotification] Failed to start notification:', error);
      return false;
    }
  }

  /**
   * Update the notification with current charging data
   */
  async updateNotification(params: ChargingNotificationUpdateParams): Promise<boolean> {
    if (Platform.OS !== 'android' || !this.isActive) {
      return false;
    }

    try {
      const timeFormatted = this.formatTime(params.timeElapsedSeconds);
      const icon = params.isCharging ? '⚡' : '🔌';
      
      // Build the notification body with key stats
      let body = `${this.chargerName}\n`;
      body += `${params.chargingStatus}\n`;
      body += `━━━━━━━━━━━━━━━━━━━━\n`;
      body += `🔋 ${params.kwhCharged.toFixed(2)} kWh`;
      
      if (params.chargingRateKw && params.chargingRateKw > 0) {
        body += ` @ ${params.chargingRateKw.toFixed(1)} kW`;
      }
      
      body += `\n⏱️ ${timeFormatted}`;
      body += `\n💰 $${params.estimatedCost.toFixed(2)} est.`;

      // Determine title based on status
      let title = `${icon} Charging Session`;
      if (params.chargingStatus.toLowerCase().includes('complete')) {
        title = '✅ Charging Complete';
      } else if (params.chargingStatus.toLowerCase().includes('fault') || 
                 params.chargingStatus.toLowerCase().includes('error')) {
        title = '⚠️ Charger Issue';
      } else if (!params.isCharging) {
        title = '🔌 Connected - Not Charging';
      }

      await Notifications.scheduleNotificationAsync({
        identifier: CHARGING_NOTIFICATION_ID,
        content: {
          title,
          body,
          data: {
            type: 'charging_session',
            orderId: this.currentOrderId,
            screen: 'ChargingScreen',
          },
          categoryIdentifier: 'charging',
          sticky: true,
          autoDismiss: false,
          priority: Notifications.AndroidNotificationPriority.HIGH,
        },
        trigger: null,
      });

      return true;
    } catch (error) {
      console.error('[AndroidChargingNotification] Failed to update notification:', error);
      return false;
    }
  }

  /**
   * Dismiss the charging notification
   */
  async dismissNotification(): Promise<boolean> {
    if (Platform.OS !== 'android') {
      return false;
    }

    try {
      await Notifications.dismissNotificationAsync(CHARGING_NOTIFICATION_ID);
      this.isActive = false;
      this.currentOrderId = null;
      console.log('[AndroidChargingNotification] Notification dismissed');
      return true;
    } catch (error) {
      console.error('[AndroidChargingNotification] Failed to dismiss notification:', error);
      return false;
    }
  }

  /**
   * Show a final "charge complete" notification before dismissing
   */
  async endWithSummary(
    finalKwh: number,
    finalCost: number,
    totalTimeSeconds: number
  ): Promise<boolean> {
    if (Platform.OS !== 'android') {
      return false;
    }

    try {
      const timeFormatted = this.formatTime(totalTimeSeconds);

      // Show completion summary (non-sticky, will auto-dismiss)
      await Notifications.scheduleNotificationAsync({
        identifier: CHARGING_NOTIFICATION_ID,
        content: {
          title: '✅ Charging Complete!',
          body: `${this.chargerName}\n` +
                `━━━━━━━━━━━━━━━━━━━━\n` +
                `🔋 Total: ${finalKwh.toFixed(2)} kWh\n` +
                `⏱️ Duration: ${timeFormatted}\n` +
                `💰 Cost: $${finalCost.toFixed(2)}`,
          data: {
            type: 'charge_completed',
            orderId: this.currentOrderId,
            screen: 'ChargingScreen',
          },
          categoryIdentifier: 'charging',
          sticky: false, // Allow dismissal
          autoDismiss: true,
        },
        trigger: null,
      });

      // Auto-dismiss after 10 seconds
      setTimeout(() => {
        this.dismissNotification();
      }, 10000);

      this.isActive = false;
      console.log('[AndroidChargingNotification] Charge complete notification shown');
      return true;
    } catch (error) {
      console.error('[AndroidChargingNotification] Failed to show completion:', error);
      return false;
    }
  }

  // --------------------------------------------------------------------------
  // Status & Info
  // --------------------------------------------------------------------------

  /**
   * Check if there's an active charging notification
   */
  isNotificationActive(): boolean {
    return this.isActive;
  }

  /**
   * Get the current order ID associated with the notification
   */
  getCurrentOrderId(): string | null {
    return this.currentOrderId;
  }

  /**
   * Check if the current notification matches the given order
   */
  isNotificationForOrder(orderId: string): boolean {
    return this.currentOrderId === orderId;
  }

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  /**
   * Format seconds to HH:MM:SS or MM:SS
   */
  private formatTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Singleton instance of the AndroidChargingNotificationService
 * Use this for all Android charging notification operations
 */
export const androidChargingNotificationService = new AndroidChargingNotificationService();

export default androidChargingNotificationService;

